import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import type { BlockerFunction } from 'react-router-dom';
import { useBlocker } from 'react-router-dom';

const titleText = 'Are you sure you want to leave this page?';
const defaultModalText = 'You have unsaved changes that will be lost if you leave this page';

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Custom text is only supported in older browsers
  return titleText;
};

interface OwnProps {
  needsConfirm: boolean;
  modalText?: string;
}

const ConfirmNavigation: FC<OwnProps> = ({ needsConfirm, modalText }) => {
  modalText = modalText ?? defaultModalText;
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => needsConfirm && currentLocation.pathname !== nextLocation.pathname,
    [needsConfirm],
  );
  const blocker = useBlocker(shouldBlock);
  const isInBlockedState = blocker.state === 'blocked';
  const isInProceedingState = blocker.state === 'proceeding';

  useEffect(() => {
    if (needsConfirm) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else if (isInBlockedState && !needsConfirm) {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      blocker.reset?.();
    }
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [blocker, needsConfirm, isInBlockedState]);

  const handleSubmit = () => blocker.proceed?.();
  const handleCancel = () => blocker.reset?.();

  return (
    <Modal
      title={
        <>
          <ExclamationCircleFilled style={{ color: 'var(--ant-color-warning)' }} />
          <span style={{ marginLeft: '.5em' }}>{titleText}</span>
        </>
      }
      okText="Yes"
      cancelText="No"
      open={isInBlockedState || isInProceedingState}
      onOk={handleSubmit}
      onCancel={handleCancel}
      maskClosable={false}
    >
      {modalText}
    </Modal>
  );
};

export default ConfirmNavigation;
